import apiClient, { API_ENDPOINTS } from './config';
import {
  ChatHistoryResponse,
  ChatMessageResponse,
  ChatSessionContext,
  ChatSessionListItem,
  ChatSessionResponse,
} from '../types';
import { getStoredAuthToken, getStoredAuthUser } from '../utils/authStorage';

export const listChatSessions = async (
  projectId: number,
  context?: ChatSessionContext | null,
): Promise<ChatSessionListItem[]> => {
  const response = await apiClient.get(API_ENDPOINTS.CHAT_SESSIONS, {
    params: {
      project_id: projectId,
      context_mode: context?.context_mode ?? undefined,
      artifact_id: context?.artifact_id ?? undefined,
      job_id: context?.job_id ?? undefined,
      test_case_id: context?.test_case_id ?? undefined,
      test_key: context?.test_key ?? undefined,
      run_id: context?.run_id ?? undefined,
      execution_key: context?.execution_key ?? undefined,
      variant_id: context?.variant_id ?? undefined,
      dataset_id: context?.dataset_id ?? undefined,
    },
  });
  return response.data;
};

export const createChatSession = async (
  projectId: number,
  sessionName?: string | null,
  context?: ChatSessionContext | null,
): Promise<ChatSessionResponse> => {
  const response = await apiClient.post(API_ENDPOINTS.CHAT_SESSIONS, {
    project_id: projectId,
    session_name: sessionName || null,
    context: context || null,
  });
  return response.data;
};

export const fetchChatHistory = async (sessionId: string): Promise<ChatHistoryResponse> => {
  const response = await apiClient.get(API_ENDPOINTS.CHAT_SESSION_HISTORY(sessionId));
  return response.data;
};

export const sendChatMessage = async (sessionId: string, message: string): Promise<ChatMessageResponse> => {
  const response = await apiClient.post(API_ENDPOINTS.CHAT_SESSION_MESSAGE(sessionId), {
    message,
  });
  return response.data;
};

export type ChatStreamEvent = {
  status: 'start' | 'streaming' | 'complete' | 'error';
  session_id?: string;
  user_message?: string;
  token?: string;
  accumulated?: string;
  full_response?: string;
  timestamp?: string;
  error?: string;
};

const ACTIVE_ORGANIZATION_HEADER = 'X-Active-Organization-Id';

function getAuthToken(): string | null {
  return getStoredAuthToken();
}

function getActiveOrganizationId(): string | null {
  try {
    const parsedUser = getStoredAuthUser<{ active_organization_id?: number | string | null }>();
    if (!parsedUser) {
      return null;
    }

    const activeOrganizationId = Number(parsedUser?.active_organization_id || 0);
    return activeOrganizationId > 0 ? String(activeOrganizationId) : null;
  } catch {
    return null;
  }
}

async function buildStreamError(response: Response): Promise<Error> {
  const fallbackMessage = `Request failed with status ${response.status}`;

  try {
    const rawText = await response.text();
    if (!rawText) {
      return new Error(fallbackMessage);
    }

    try {
      const parsed = JSON.parse(rawText);
      const detail = parsed?.detail || parsed?.error || parsed?.message;
      if (typeof detail === 'string' && detail.trim()) {
        return new Error(detail);
      }
    } catch {
      // Response was not JSON; use the raw text below.
    }

    return new Error(rawText);
  } catch {
    return new Error(fallbackMessage);
  }
}

const parseSseEvents = (chunk: string): ChatStreamEvent[] => {
  const events: ChatStreamEvent[] = [];
  const lines = chunk.split('\n');
  const dataLines = lines
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.replace(/^data:\s?/, ''));
  if (dataLines.length === 0) {
    return events;
  }
  const payload = dataLines.join('\n').trim();
  if (!payload) {
    return events;
  }
  try {
    const parsed = JSON.parse(payload) as ChatStreamEvent;
    if (parsed?.status) {
      events.push(parsed);
    }
  } catch {
    // Ignore malformed chunks
  }
  return events;
};

export const streamChatMessage = async (
  sessionId: string,
  message: string,
  onEvent: (event: ChatStreamEvent) => void,
  options?: { signal?: AbortSignal },
): Promise<void> => {
  const token = getAuthToken();
  const activeOrganizationId = getActiveOrganizationId();
  const baseUrl = apiClient.defaults.baseURL || '';
  const response = await fetch(`${baseUrl}${API_ENDPOINTS.CHAT_SESSION_MESSAGE_STREAM(sessionId)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(activeOrganizationId ? { [ACTIVE_ORGANIZATION_HEADER]: activeOrganizationId } : {}),
    },
    body: JSON.stringify({ message }),
    signal: options?.signal,
  });

  if (!response.ok) {
    throw await buildStreamError(response);
  }

  if (!response.body) {
    throw new Error('Streaming response is not supported by the browser.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });

    let boundaryIndex = buffer.indexOf('\n\n');
    while (boundaryIndex !== -1) {
      const chunk = buffer.slice(0, boundaryIndex);
      buffer = buffer.slice(boundaryIndex + 2);
      const events = parseSseEvents(chunk);
      events.forEach(onEvent);
      boundaryIndex = buffer.indexOf('\n\n');
    }
  }

  if (buffer.trim()) {
    const events = parseSseEvents(buffer);
    events.forEach(onEvent);
  }
};

export const deleteChatSession = async (sessionId: string, projectId: number): Promise<{ message: string; session_id: string }> => {
  const response = await apiClient.delete(API_ENDPOINTS.CHAT_SESSION_DELETE(sessionId), {
    params: { project_id: projectId },
  });
  return response.data;
};
